// Site-Gate: Bevorzugt Single-Login übers Sickinger-Portal (sk_session-Cookie).
// Wer keine gültige Portal-Session hat, wird zum Portal geschickt.
// Fallback: altes Site-Passwort 71735 (Notzugang) über einen kleinen Link.
import { useEffect, useState } from 'react';

interface Props { children: React.ReactNode }

const PORTAL_URL = 'https://sickinger-portal.de';

export function PasswordGate({ children }: Props) {
  const [state, setState] = useState<'checking'|'locked'|'ok'>('checking');
  const [showFallback, setShowFallback] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Başlangıç ping — gültige Session (Portal sk_session ODER Site-Cookie) vorhanden?
  useEffect(() => {
    fetch('/api/lookup/abteilungen', { credentials: 'include' })
      .then(r => setState(r.status === 401 ? 'locked' : 'ok'))
      .catch(() => setState('locked'));
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const r = await fetch('/api/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (!r.ok) {
        const b = await r.json().catch(() => ({ error: 'Fehler' }));
        throw new Error((b as { error?: string }).error ?? 'Falsches Passwort');
      }
      setState('ok');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fehler');
    } finally {
      setLoading(false);
    }
  };

  if (state === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-slate-500">Lädt …</div>
      </div>
    );
  }

  if (state === 'locked') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-700">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm">
          <div className="flex items-center gap-3 mb-6">
            <img src="/favicon.svg" alt="MS" className="w-12 h-12 rounded-lg" />
            <div>
              <div className="font-bold text-slate-900">Manfred Sickinger GmbH</div>
              <div className="text-xs text-slate-500">
                {typeof window !== 'undefined' && window.location.hostname.startsWith('einkauf.') ? 'Einkauf'
                 : typeof window !== 'undefined' && window.location.hostname.startsWith('blechbedarf.') ? 'Blechbedarf'
                 : 'Terminliste 2026'}
              </div>
            </div>
          </div>

          {!showFallback ? (
            <>
              <h1 className="text-lg font-semibold text-slate-900 mb-1">Anmeldung erforderlich</h1>
              <p className="text-sm text-slate-500 mb-4">
                Bitte über das Sickinger-Portal anmelden — ein Login gilt für alle Module.
              </p>
              <a href={PORTAL_URL}
                 className="block w-full text-center bg-sky-600 hover:bg-sky-700 text-white rounded-lg py-3 font-semibold">
                Zum Portal-Login
              </a>
              <button type="button" onClick={() => setShowFallback(true)}
                      className="w-full text-xs text-slate-400 hover:text-slate-600 mt-4">
                Mit Site-Passwort anmelden (Notzugang)
              </button>
            </>
          ) : (
            <form onSubmit={submit}>
              <h1 className="text-lg font-semibold text-slate-900 mb-1">Notzugang</h1>
              <p className="text-sm text-slate-500 mb-4">Site-Passwort eingeben</p>
              <input type="password"
                     autoFocus
                     value={password}
                     onChange={e => setPassword(e.target.value)}
                     placeholder="Passwort"
                     className="w-full border border-slate-300 rounded-lg px-4 py-3 text-lg mb-3 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent" />
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-2 text-sm mb-3">
                  {error}
                </div>
              )}
              <button type="submit" disabled={loading || !password}
                      className="w-full bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white rounded-lg py-3 font-semibold">
                {loading ? 'Prüft …' : 'Einloggen'}
              </button>
              <button type="button" onClick={() => setShowFallback(false)}
                      className="w-full text-xs text-slate-400 hover:text-slate-600 mt-3">
                ← Zurück zum Portal-Login
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
